/**
 * EPIC-024 STORY-665 — Help center starter knowledge base.
 *
 * Static, in-bundle article registry. No DB / no MDX heavyweight —
 * closed-beta scope is the rendering shell + a small starter set.
 * Founder edits content by editing this file (or splitting per
 * article into its own file if/when the count grows).
 *
 * Phase 2 (STORY-669): Postgres FTS search wires onto the same
 * registry. Phase 3 (post-launch): per-region overrides for legal
 * disclosure variations.
 *
 * Bilingual: every article carries pt + en bodies. The frontend
 * picks via useDisplayLocale (which already drives BetaBanner +
 * SiteFooter).
 */

export interface HelpArticleBody {
  intro: string;
  sections: Array<{
    heading: string;
    paragraphs: string[];
    bullets?: string[];
  }>;
}

export interface HelpArticle {
  slug: string;
  category: HelpCategory;
  // Shown in the left-nav, header, and search results.
  title: { en: string; pt: string };
  // 1-line summary in the index page.
  description: { en: string; pt: string };
  body: { en: HelpArticleBody; pt: HelpArticleBody };
  updatedAt: string; // ISO date when last edited
}

export type HelpCategory =
  | "getting-started"
  | "trading"
  | "account"
  | "fees"
  | "security";

// 2026-05-02 — emoji icons → lucide line icons to align with the
// Vercel-aesthetic identity baseline (PR #537 + #800). Type changed
// from `icon: string` to `icon: LucideIcon`; the consumer at
// /help/page.tsx renders `<cat.Icon size={14} strokeWidth={1.75}/>`.
import {
  BarChart3,
  Coins,
  Rocket,
  ShieldCheck,
  User,
  type LucideIcon,
} from "lucide-react";

export const HELP_CATEGORIES: Array<{
  id: HelpCategory;
  Icon: LucideIcon;
  label: { en: string; pt: string };
}> = [
  {
    id: "getting-started",
    Icon: Rocket,
    label: { en: "Getting started", pt: "Primeiros passos" },
  },
  {
    id: "trading",
    Icon: BarChart3,
    label: { en: "Trading", pt: "Operações" },
  },
  {
    id: "account",
    Icon: User,
    label: { en: "Account", pt: "Conta" },
  },
  { id: "fees", Icon: Coins, label: { en: "Fees", pt: "Tarifas" } },
  {
    id: "security",
    Icon: ShieldCheck,
    label: { en: "Security & privacy", pt: "Segurança & privacidade" },
  },
];

export const HELP_ARTICLES: HelpArticle[] = [
  {
    slug: "market-resolution",
    category: "trading",
    title: {
      en: "How market resolution works",
      pt: "Como funciona a resolução de mercados",
    },
    description: {
      en: "Who resolves a market, when, and what happens to your shares.",
      pt: "Quem resolve um mercado, quando, e o que acontece com suas ações.",
    },
    updatedAt: "2026-05-01",
    body: {
      en: {
        intro:
          "Every market on Granbay has an end date — a date after which the underlying question can be answered with a definitive YES or NO. Resolution is the process of recording that answer and paying out share holders accordingly.",
        sections: [
          {
            heading: "Who resolves",
            paragraphs: [
              "During the closed beta a single Granbay admin resolves each market. Every resolution requires a written reason (≥30 chars) and an evidence URL pointing to a credible source — both are stored permanently on the market detail page so anyone can audit the call.",
              "Before the public real-money launch (Gate 3), Granbay shifts to a multi-resolver model: at least two admins must agree on the outcome and a tiebreaker procedure handles disagreements. Until then, the closed-beta single-admin model carries the safeguards above + a dispute window for any user who believes a call was wrong.",
            ],
          },
          {
            heading: "When does it happen",
            paragraphs: [
              "Resolution can fire any time after the market's stated `closes_at` date, once the underlying question has a verifiable answer. Some markets resolve within minutes (e.g. a sports game's final score); others take days (e.g. a court ruling published the following week).",
              "If you want to track which markets are pending resolution, the market detail page shows a 'Closed — pending resolution' state once `closes_at` has passed but the admin hasn't recorded an outcome yet.",
            ],
          },
          {
            heading: "What happens to your shares",
            paragraphs: [
              "On resolution, every YES share you hold pays out 100¢ if the resolved outcome was YES — and 0¢ if it was NO. NO shares mirror this: they pay 100¢ if the outcome was NO, 0¢ if it was YES. The payout fires in a single ledger transaction so your wallet balance updates atomically with the market's status flip.",
              "If a market is voided (e.g. the question becomes un-answerable), every share is refunded at its purchase price. The 2% taker fee paid at trade time is not refunded — it covered the matching service rendered, regardless of the eventual outcome. See the Fees article for details.",
            ],
          },
          {
            heading: "Disputing a resolution",
            paragraphs: [
              "If you believe a resolution was wrong, file a dispute from the market detail page within 7 days of resolution. Provide a written reason (≥30 chars) and ideally an evidence URL pointing to a counter-source. Disputes are reviewed by a separate admin (not the resolver) and either upheld (resolution reversed, payouts re-run) or rejected with a written response.",
              "While a dispute is under review the resolution stands — no payout claw-back. If the dispute is upheld, the reversal posts new ledger entries that net out the prior payout and apply the corrected outcome.",
            ],
          },
        ],
      },
      pt: {
        intro:
          "Cada mercado na Granbay tem uma data final — uma data após a qual a pergunta pode ser respondida com um SIM ou NÃO definitivo. A resolução é o processo de registrar essa resposta e pagar os detentores de ações.",
        sections: [
          {
            heading: "Quem resolve",
            paragraphs: [
              "Durante o beta fechado, um único administrador da Granbay resolve cada mercado. Toda resolução exige uma justificativa por escrito (≥30 caracteres) e uma URL com a evidência apontando para uma fonte confiável — ambos ficam permanentemente na página do mercado para qualquer um auditar.",
              "Antes do lançamento público com dinheiro real (Gate 3), a Granbay migra para o modelo multi-resolutor: pelo menos dois administradores precisam concordar com o resultado e um procedimento de desempate trata divergências. Até lá, o modelo single-admin carrega as proteções acima + uma janela de disputa para qualquer usuário que acredite que a decisão foi errada.",
            ],
          },
          {
            heading: "Quando acontece",
            paragraphs: [
              "A resolução pode ser disparada a qualquer momento após a data `closes_at` do mercado, desde que a pergunta tenha resposta verificável. Alguns mercados resolvem em minutos (ex.: placar final de jogo); outros levam dias (ex.: decisão judicial publicada na semana seguinte).",
              "Se você quer acompanhar quais mercados estão pendentes, a página do mercado mostra um estado 'Fechado — aguardando resolução' assim que o `closes_at` passa mas o admin ainda não registrou o resultado.",
            ],
          },
          {
            heading: "O que acontece com suas ações",
            paragraphs: [
              "Na resolução, cada ação SIM que você tem paga 100¢ se o resultado foi SIM — e 0¢ se foi NÃO. Ações NÃO espelham essa lógica: pagam 100¢ se o resultado foi NÃO e 0¢ se foi SIM. O pagamento dispara em uma única transação contábil, então o saldo da sua carteira é atualizado atomicamente com a mudança de status do mercado.",
              "Se um mercado é anulado (a pergunta se torna não respondível), cada ação é reembolsada pelo preço de compra. A tarifa de 2% paga no momento da operação não é devolvida — ela cobriu o serviço de matching prestado, independentemente do resultado final. Veja o artigo Tarifas para detalhes.",
            ],
          },
          {
            heading: "Disputando uma resolução",
            paragraphs: [
              "Se você acredita que uma resolução foi errada, abra uma disputa na página do mercado em até 7 dias após a resolução. Forneça uma justificativa por escrito (≥30 caracteres) e idealmente uma URL com a contra-evidência. Disputas são revisadas por um administrador diferente (não o resolutor) e ou são acolhidas (resolução revertida, pagamentos refeitos) ou rejeitadas com uma resposta por escrito.",
              "Enquanto a disputa está em análise, a resolução vale — sem estorno do pagamento. Se a disputa for acolhida, a reversão posta novos lançamentos contábeis que zeram o pagamento anterior e aplicam o resultado corrigido.",
            ],
          },
        ],
      },
    },
  },
  {
    slug: "kyc-and-identity-verification",
    category: "account",
    title: {
      en: "KYC & identity verification",
      pt: "KYC & verificação de identidade",
    },
    description: {
      en: "Why KYC isn't required during closed beta and what to expect when real-money operations open.",
      pt: "Por que o KYC não é exigido no beta fechado e o que esperar quando operações com dinheiro real abrirem.",
    },
    updatedAt: "2026-05-01",
    body: {
      en: {
        intro:
          "KYC (Know-Your-Customer) is the regulatory requirement for prediction-market platforms to verify the identity of users before they can deposit, withdraw, or trade with real money. During the closed-beta phase Granbay runs entirely on virtual cash, so no KYC is required at signup.",
        sections: [
          {
            heading: "When KYC fires",
            paragraphs: [
              "KYC activates automatically the first time you interact with real-money operations: deposits, withdrawals, or real-cash trading. The closed-beta phase doesn't trigger any of those, so you can sign up, trade with the R$1,000 virtual grant, follow Popular Investors, and explore every product surface without uploading any documents.",
              "When real money opens (after the Sprint 11/12 validation gate), the first deposit attempt will route through a verification flow before crediting your wallet. Read on for what that involves.",
            ],
          },
          {
            heading: "What we'll ask for",
            paragraphs: [
              "Tier 1 (the entry-level verification that unlocks deposits + withdrawals): a government-issued photo ID (CPF / RG for Brazilian users; passport / driver's license elsewhere) plus a live selfie that the verification provider matches against the ID photo. The whole flow takes 2–5 minutes from your phone.",
              "Tier 2 (only required for higher-volume traders or specific regulatory thresholds): proof of address (utility bill or bank statement, dated within 90 days). You won't see this prompt unless your trading volume crosses the threshold or your jurisdiction requires it.",
            ],
          },
          {
            heading: "Who handles the documents",
            paragraphs: [
              "Granbay uses Sumsub as the verification provider. They are the same vendor used by major exchanges (Binance, Kraken, Mercado Bitcoin) and are licensed under EU + UK regulators for KYC operations. Your documents go straight to Sumsub's secure pipeline; Granbay only stores the verification result (pass / fail) and a reference token, not your raw ID images.",
              "Sumsub's privacy policy and data-retention windows govern how long the documents are kept. Granbay's own data-retention policy on the result token is described in the Account deletion (LGPD) article.",
            ],
          },
          {
            heading: "What happens if KYC fails",
            paragraphs: [
              "Most rejections are recoverable: blurry photo, ID partially out of frame, lighting too dim. The verification flow tells you the specific issue and lets you re-submit immediately. Repeated rejections (3+) escalate to manual review by Granbay support — expect a 1–3 business day turnaround.",
              "If your jurisdiction is on the unsupported list (see Supported regions), KYC will reject regardless of document quality and you'll see a 'Region not supported' message. We can't override this; the geographic gating is part of the regulatory contract.",
            ],
          },
        ],
      },
      pt: {
        intro:
          "KYC (Conheça-Seu-Cliente) é a exigência regulatória para plataformas de mercados de previsão verificarem a identidade dos usuários antes de depósitos, saques ou operações com dinheiro real. Durante o beta fechado a Granbay opera só com dinheiro virtual, então nenhum KYC é exigido no cadastro.",
        sections: [
          {
            heading: "Quando o KYC dispara",
            paragraphs: [
              "O KYC ativa automaticamente na primeira vez que você interage com operações em dinheiro real: depósitos, saques ou trading com dinheiro real. O beta fechado não dispara nenhuma dessas coisas, então você pode se cadastrar, operar com o saldo virtual de R$1.000, seguir Investidores Populares e explorar toda a superfície do produto sem enviar nenhum documento.",
              "Quando o dinheiro real abrir (após a etapa de validação do Sprint 11/12), a primeira tentativa de depósito vai passar por um fluxo de verificação antes de creditar sua carteira. Continue lendo para saber o que isso envolve.",
            ],
          },
          {
            heading: "O que vamos pedir",
            paragraphs: [
              "Nível 1 (verificação básica que libera depósitos + saques): um documento de identidade com foto emitido pelo governo (CPF / RG para brasileiros; passaporte / CNH para outros países) mais uma selfie ao vivo que o provedor compara com a foto do documento. O fluxo todo leva 2–5 minutos pelo celular.",
              "Nível 2 (só necessário para volumes maiores ou limites regulatórios específicos): comprovante de endereço (conta de luz ou extrato bancário, datado nos últimos 90 dias). Você só vai ver esse pedido se o volume cruzar o limite ou se sua jurisdição exigir.",
            ],
          },
          {
            heading: "Quem cuida dos documentos",
            paragraphs: [
              "A Granbay usa o Sumsub como provedor de verificação. É o mesmo fornecedor usado por grandes corretoras (Binance, Kraken, Mercado Bitcoin) e tem licença reguladora na UE + Reino Unido para operações de KYC. Seus documentos vão direto para o pipeline seguro do Sumsub; a Granbay só armazena o resultado da verificação (passou / falhou) e um token de referência, não as imagens cruas.",
              "A política de privacidade e a janela de retenção de dados do Sumsub governam quanto tempo os documentos ficam. A política da Granbay sobre o token de resultado está descrita no artigo Exclusão de conta (LGPD).",
            ],
          },
          {
            heading: "O que acontece se o KYC falhar",
            paragraphs: [
              "A maioria das rejeições é recuperável: foto borrada, documento cortado, iluminação ruim. O fluxo informa o problema específico e permite reenviar na hora. Rejeições repetidas (3+) escalam para revisão manual pelo suporte — espere 1–3 dias úteis.",
              "Se sua jurisdição está na lista de regiões não suportadas (veja Regiões suportadas), o KYC vai rejeitar independentemente da qualidade do documento e você vai ver uma mensagem 'Região não suportada'. Não podemos contornar isso; o bloqueio geográfico faz parte do contrato regulatório.",
            ],
          },
        ],
      },
    },
  },
  {
    slug: "supported-regions",
    category: "account",
    title: {
      en: "Supported regions",
      pt: "Regiões suportadas",
    },
    description: {
      en: "Which countries can use Granbay today and how the region you're in affects what you see.",
      pt: "Quais países podem usar a Granbay hoje e como a sua região afeta o que você vê.",
    },
    updatedAt: "2026-05-03",
    body: {
      en: {
        intro:
          "Granbay is rolling out region by region. Phase 1 covers four launch markets — Brazil, Mexico, Canada (excluding Ontario), and India. Your region is detected automatically from your IP address on first visit, lands you on the right language and currency display, and persists in your account preferences after. You can switch regions any time via the flag picker in the header.",
        sections: [
          {
            heading: "Phase 1 launch markets",
            paragraphs: [
              "Brazil — Portuguese UI, BRL projection layer over the USDC-denominated balance, PIX-primary deposit flow (Asaas), CPF-aware KYC. The aunt-from-Brazil persona test (ADR-007 / ADR-008) is the acceptance criterion for that experience.",
              "Mexico — Spanish (es-MX) UI, USDC-primary display, SPEI deposit/withdrawal once real-money flips on. Launch order #1 — Mexico is the first non-Brazil region to flip the real-money switch (Gate B, post-validation).",
              "Canada (excluding Ontario) — Canadian English (en-CA) by default, Quebec French (fr-CA) when your browser prefers it, USDC-primary display, Plaid + Interac e-Transfer at deposit time. Ontario is excluded from Phase 1 due to provincial regulatory specifics.",
              "India — English (en-IN) UI, USDC-primary display, Razorpay/Cashfree on-ramp planned with the 1% TDS withholding required by Indian tax law surfaced transparently at deposit time.",
              "Detection runs via Cloudflare's CF-IPCountry header. The first paint after that detection writes a granbay.region cookie; subsequent visits read the cookie. You can switch any time via the flag picker in the header — the picked locale carries detected:false so the auto-detection won't override your choice.",
            ],
          },
          {
            heading: "Outside Phase 1",
            paragraphs: [
              "If your IP isn't in a Phase 1 market, you land on the default English UI with USDC-primary display and no jurisdiction-specific compliance copy. Real-money signup is gated to Phase 1 markets in the closed beta; non-Phase-1 visitors see a 'Coming soon to your region' notice + waitlist when they hit deposit / withdrawal flows. Trading the virtual-cash flow during the closed beta is unrestricted globally.",
              "Some jurisdictions are blocked entirely from signup due to regulatory ambiguity or explicit prohibition on prediction markets: mainland China, Iran, North Korea, Russia, Cuba, and Sudan. The signup flow rejects accounts created from those IPs. OFAC-sanctioned individuals are excluded everywhere — even a Phase 1 signup can fail KYC if the user appears on a sanctions list.",
            ],
          },
          {
            heading: "What the region controls",
            paragraphs: [
              "Region drives four things: (1) the UI language (per the Phase 1 mapping above; every user can override via the picker), (2) the displayed currency framing (BRL projection for Brazil; USDC primary everywhere else, with optional inline conversion to MXN/CAD/INR/USD per /settings/display), (3) the payment-rail menu at deposit / withdrawal time (PIX for Brazil, SPEI for Mexico, Interac for Canada, UPI for India — only fires once real-money operations open), and (4) the jurisdiction-specific compliance framing in the privacy policy, terms of service, and KYC disclosures (LGPD for Brazil, LFPDPPP for Mexico, PIPEDA for Canada, DPDPA + 1% TDS for India, generic industry-standard for everywhere else).",
              "Region does NOT control which markets you see — the market list is global. A Brazilian user can trade a US-politics market and a Mexican user can trade a Brazilian-football market, each in their own preferred locale.",
            ],
          },
          {
            heading: "Changing region",
            paragraphs: [
              "Use the flag picker in the site header to switch regions any time. The choice writes to the granbay.region cookie with detected:false, which pins the override and stops the IP-based auto-detection from second-guessing you. Backend persistence (when authed) saves the locale on your account so the choice follows you across devices.",
              "If you've moved permanently and want a Phase 1 region you can't IP-detect into (e.g., a Brazilian user travelling abroad who wants the Brazilian framing), the flag picker is the answer — there's no support flow to navigate. VPN-driven region spoofing for KYC-bypass purposes is a violation of the terms of service and will result in account suspension. The geographic gating is real and load-bearing for our regulatory standing.",
            ],
          },
        ],
      },
      pt: {
        intro:
          "A Granbay está se expandindo região por região. A Fase 1 cobre quatro mercados de lançamento — Brasil, México, Canadá (exceto Ontário) e Índia. Sua região é detectada automaticamente pelo seu IP na primeira visita, te leva para o idioma e a moeda corretos, e fica salva nas preferências da conta depois disso. Você pode trocar de região a qualquer momento pelo seletor de bandeira no cabeçalho.",
        sections: [
          {
            heading: "Mercados de lançamento da Fase 1",
            paragraphs: [
              "Brasil — Interface em português, projeção em BRL sobre o saldo denominado em USDC, fluxo de depósito PIX-primário (Asaas), KYC com CPF. O teste da persona 'tia brasileira' (ADR-007 / ADR-008) é o critério de aceite dessa experiência.",
              "México — Interface em espanhol (es-MX), exibição primária em USDC, SPEI para depósito/saque quando o dinheiro real for ativado. Primeiro na ordem de lançamento — o México é a primeira região fora do Brasil a ativar o dinheiro real (Gate B, pós-validação).",
              "Canadá (exceto Ontário) — Inglês canadense (en-CA) por padrão, francês quebequense (fr-CA) quando o navegador pedir, exibição primária em USDC, Plaid + Interac e-Transfer no depósito. Ontário fica de fora da Fase 1 por especificidades regulatórias provinciais.",
              "Índia — Interface em inglês (en-IN), exibição primária em USDC, on-ramp via Razorpay/Cashfree previsto com a retenção de 1% de TDS exigida pela legislação tributária indiana exibida de forma transparente no depósito.",
              "A detecção usa o header CF-IPCountry da Cloudflare. A primeira renderização após a detecção grava o cookie granbay.region; visitas seguintes leem o cookie. Você pode trocar a qualquer momento pelo seletor de bandeira no cabeçalho — a escolha manual carrega detected:false para que a detecção automática não sobrescreva sua decisão.",
            ],
          },
          {
            heading: "Fora da Fase 1",
            paragraphs: [
              "Se o seu IP não está em um mercado da Fase 1, você cai na interface padrão em inglês com exibição primária em USDC e sem textos de conformidade específicos de jurisdição. O cadastro com dinheiro real é restrito aos mercados da Fase 1 no beta fechado; visitantes fora da Fase 1 veem um aviso 'Em breve na sua região' + lista de espera ao tentar depositar / sacar. O fluxo de saldo virtual durante o beta fechado está aberto globalmente.",
              "Algumas jurisdições têm o cadastro bloqueado por ambiguidade regulatória ou proibição explícita de mercados de previsão: China continental, Irã, Coreia do Norte, Rússia, Cuba e Sudão. O fluxo de cadastro rejeita contas criadas desses IPs. Pessoas sancionadas pela OFAC são excluídas em todos os lugares — mesmo um cadastro da Fase 1 pode falhar no KYC se o usuário aparecer em lista de sanções.",
            ],
          },
          {
            heading: "O que a região controla",
            paragraphs: [
              "A região controla quatro coisas: (1) o idioma da interface (conforme o mapeamento da Fase 1 acima; todo usuário pode sobrescrever pelo seletor), (2) o enquadramento da moeda exibida (projeção BRL para o Brasil; USDC primário em todo o resto, com conversão opcional para MXN/CAD/INR/USD via /settings/display), (3) o menu de meios de pagamento no depósito / saque (PIX para o Brasil, SPEI para o México, Interac para o Canadá, UPI para a Índia — só dispara quando o dinheiro real abrir), e (4) o enquadramento de conformidade específico da jurisdição na política de privacidade, termos de serviço e divulgações de KYC (LGPD para o Brasil, LFPDPPP para o México, PIPEDA para o Canadá, DPDPA + TDS de 1% para a Índia, padrão genérico do setor para os demais).",
              "A região NÃO controla quais mercados você vê — a lista de mercados é global. Um usuário brasileiro pode operar um mercado de política americana e um usuário mexicano pode operar um mercado de futebol brasileiro, cada um no seu idioma preferido.",
            ],
          },
          {
            heading: "Trocar de região",
            paragraphs: [
              "Use o seletor de bandeira no cabeçalho para trocar de região quando quiser. A escolha grava o cookie granbay.region com detected:false, o que fixa a sobrescrita e impede que a detecção por IP questione sua decisão. A persistência no backend (quando logado) salva o locale na sua conta para que a escolha te acompanhe entre dispositivos.",
              "Se você se mudou definitivamente e quer uma região da Fase 1 que o IP não detecta (por exemplo, um brasileiro viajando que quer o enquadramento brasileiro), o seletor de bandeira é a resposta — não há fluxo de suporte para navegar. Falsificar a região via VPN para burlar o KYC viola os termos de serviço e resulta em suspensão da conta. O bloqueio geográfico é real e essencial para a nossa conformidade regulatória.",
            ],
          },
        ],
      },
    },
  },
  {
    slug: "what-is-granbay",
    category: "getting-started",
    title: {
      en: "What is Granbay?",
      pt: "O que é a Granbay?",
    },
    description: {
      en: "A primer on prediction markets and how Granbay works.",
      pt: "Uma introdução aos mercados de previsão e como a Granbay funciona.",
    },
    updatedAt: "2026-04-29",
    body: {
      en: {
        intro:
          "Granbay is a prediction market platform. You buy and sell shares in real-world outcomes — politics, sports, crypto, culture — and the share price reflects the probability the market assigns to that outcome.",
        sections: [
          {
            heading: "How it works",
            paragraphs: [
              "Each market has two outcomes (YES / NO) priced between 1¢ and 99¢. The price is the implied probability — a YES at 65¢ means the market thinks there's a 65% chance of YES.",
              "When the market resolves, every YES share pays out 100¢ if the outcome happened, 0¢ if it didn't. The same applies to NO in mirror.",
            ],
          },
          {
            heading: "Closed beta",
            paragraphs: [
              "Granbay is currently in closed beta on virtual cash. You get a R$1,000 virtual balance at signup. Real-money operations are gated behind the Sprint 11/12 validation gate — see the BETA banner at the top of every page.",
            ],
          },
        ],
      },
      pt: {
        intro:
          "A Granbay é uma plataforma de mercados de previsão. Você compra e vende ações em resultados do mundo real — política, esportes, cripto, cultura — e o preço reflete a probabilidade que o mercado atribui ao resultado.",
        sections: [
          {
            heading: "Como funciona",
            paragraphs: [
              "Cada mercado tem dois resultados (SIM / NÃO) com preço entre 1¢ e 99¢. O preço é a probabilidade implícita — um SIM a 65¢ significa que o mercado acha que há 65% de chance de SIM.",
              "Quando o mercado é resolvido, cada ação SIM paga 100¢ se o resultado aconteceu e 0¢ caso contrário. Para NÃO vale a regra inversa.",
            ],
          },
          {
            heading: "Beta fechado",
            paragraphs: [
              "A Granbay está em beta fechado com dinheiro virtual. Você recebe R$1.000 virtuais no cadastro. Operações com dinheiro real dependem da etapa de validação do Sprint 11/12 — veja o banner BETA no topo de cada página.",
            ],
          },
        ],
      },
    },
  },
  {
    slug: "placing-your-first-trade",
    category: "trading",
    title: {
      en: "Placing your first trade",
      pt: "Sua primeira operação",
    },
    description: {
      en: "Walk through the trade panel: market vs limit, slippage, fees.",
      pt: "Conheça o painel de operações: mercado vs limite, slippage, tarifas.",
    },
    updatedAt: "2026-04-29",
    body: {
      en: {
        intro:
          "The trade panel on every market page lets you buy or sell shares in either outcome. Two flavors: market (fill at the best available price now) or limit (rest on the book at your specific price until someone matches).",
        sections: [
          {
            heading: "Market vs limit",
            paragraphs: [
              "A market BUY fills at the cheapest available ask, walking up the book until your size is filled. A limit BUY at 50¢ rests on the book until a SELL at 50¢ or better matches it.",
              "Use market when you want certainty of execution and don't mind paying the spread. Use limit when you have a price you won't go above.",
            ],
          },
          {
            heading: "Slippage protection",
            paragraphs: [
              "Market orders carry a maxSlippageCents tolerance. By default, the matcher refuses fills more than 2¢ from the outcome's last-trade price. Adjust your default at /settings/trading or override per-order.",
              "If a fill would exceed the tolerance, the matcher leaves the unfilled portion as a limit order at the anchor price (auto-convert), or cancels it (strict mode — toggle at /settings/trading).",
            ],
          },
          {
            heading: "Fees",
            paragraphs: [
              "Granbay charges 2% to the taker (the side that crossed the spread) and nothing to the maker (the resting order that gets hit). Fee is taken at fill time from the taker's cash. See the Fees article for the full rate table.",
            ],
          },
        ],
      },
      pt: {
        intro:
          "O painel de operações em cada mercado permite comprar ou vender ações em qualquer resultado. Dois tipos: mercado (executa no melhor preço disponível agora) ou limite (fica no livro no preço que você escolheu até alguém igualar).",
        sections: [
          {
            heading: "Mercado vs limite",
            paragraphs: [
              "Uma COMPRA a mercado executa no menor ask disponível, subindo no livro até o seu tamanho ser preenchido. Uma COMPRA limite a 50¢ fica no livro até uma VENDA a 50¢ ou melhor igualar.",
              "Use mercado quando quer execução garantida e aceita pagar o spread. Use limite quando tem um preço-teto.",
            ],
          },
          {
            heading: "Proteção de slippage",
            paragraphs: [
              "Operações a mercado carregam uma tolerância maxSlippageCents. Por padrão, o motor recusa execuções a mais de 2¢ do último preço do resultado. Ajuste em /settings/trading ou por ordem.",
              "Se uma execução exceder a tolerância, o motor deixa a parte não executada como ordem limite no preço-âncora (auto-conversão), ou cancela (modo estrito — alterne em /settings/trading).",
            ],
          },
          {
            heading: "Tarifas",
            paragraphs: [
              "A Granbay cobra 2% do taker (lado que cruza o spread) e nada do maker (ordem que descansa no livro). A tarifa é descontada no momento da execução. Veja o artigo Tarifas para a tabela completa.",
            ],
          },
        ],
      },
    },
  },
  {
    slug: "deposits-and-withdrawals",
    category: "account",
    title: {
      en: "Deposits & withdrawals",
      pt: "Depósitos & saques",
    },
    description: {
      en: "Why deposits and withdrawals are disabled during closed beta.",
      pt: "Por que depósitos e saques estão desativados durante o beta fechado.",
    },
    updatedAt: "2026-04-29",
    body: {
      en: {
        intro:
          "Deposits and withdrawals are disabled during the closed-beta phase. Granbay runs on virtual cash today and real-money operations only activate after the Sprint 11/12 validation gate passes (per ADR-014 / ADR-015).",
        sections: [
          {
            heading: "When will real money open?",
            paragraphs: [
              "After 30+ users have actively traded for 4+ consecutive weeks. The validation gate measures real engagement on the existing virtual-cash mechanics. If it passes, real-money infrastructure (KYC, payment rails, custody) activates without changing the trading code paths.",
              "Top virtual performers receive permanent referral-tier upgrades, recognition badges, and 90 days fee-free real-money trading at the transition. There is no cash conversion of virtual balances (ADR-016).",
            ],
          },
          {
            heading: "What happens to my virtual balance?",
            paragraphs: [
              "Virtual balances persist through the beta. They reset on the configured reset day (announced in advance) so the platform starts real-money operations on a clean slate. You don't lose access — the balance just cannot be withdrawn or converted to fiat.",
            ],
          },
        ],
      },
      pt: {
        intro:
          "Depósitos e saques estão desativados durante a fase de beta fechado. A Granbay opera com dinheiro virtual hoje e operações com dinheiro real só ativam após a etapa de validação do Sprint 11/12 (conforme ADR-014 / ADR-015).",
        sections: [
          {
            heading: "Quando o dinheiro real abre?",
            paragraphs: [
              "Após 30+ usuários operarem ativamente por 4+ semanas consecutivas. A etapa de validação mede engajamento real nos mecanismos atuais com dinheiro virtual. Se passar, a infraestrutura de dinheiro real (KYC, meios de pagamento, custódia) ativa sem mudar os caminhos de código de operação.",
              "Os melhores traders virtuais ganham upgrades permanentes de nível de indicação, selos de reconhecimento e 90 dias sem tarifa em dinheiro real na transição. Não há conversão dos saldos virtuais em dinheiro (ADR-016).",
            ],
          },
          {
            heading: "O que acontece com meu saldo virtual?",
            paragraphs: [
              "Os saldos virtuais permanecem durante o beta. Eles são zerados no dia de reset configurado (anunciado com antecedência) para que a plataforma comece operações com dinheiro real do zero. Você não perde o acesso — o saldo apenas não pode ser sacado ou convertido em moeda.",
            ],
          },
        ],
      },
    },
  },
  {
    slug: "account-deletion-lgpd",
    category: "account",
    title: {
      en: "Account deletion (LGPD)",
      pt: "Exclusão de conta (LGPD)",
    },
    description: {
      en: "How to request account deletion and what gets removed.",
      pt: "Como solicitar a exclusão da sua conta e o que é removido.",
    },
    updatedAt: "2026-04-29",
    body: {
      en: {
        intro:
          "Granbay complies with the Brazilian LGPD (Lei Geral de Proteção de Dados). You can request your account be deleted at any time from /settings/account.",
        sections: [
          {
            heading: "How it works",
            paragraphs: [
              "Deletion runs in two phases. First, you submit a request with a reason. We immediately revoke all active sessions and lock the account. A 30-day grace period starts during which you can cancel the deletion by logging back in.",
              "After the 30-day window, identifying fields (email, username, country, password hash, 2FA secret) are anonymized and the account is fully deleted. Trade history, ledger entries, and audit events are retained in anonymized form for accounting and regulatory compliance.",
            ],
          },
          {
            heading: "What we keep",
            paragraphs: [
              "Trade and order records are retained per accounting compliance — they are required to reconstruct counterparty positions, fee accounting, and resolve any historical disputes. After anonymization the records cannot be traced back to you.",
              "You can download a full export of your data before deletion via /settings/account → Download my data.",
            ],
          },
          {
            heading: "Cancelling a request",
            paragraphs: [
              "Log back in anytime in the 30-day window. The auth flow recognises pending-deletion accounts and lets you cancel the request without re-verification. Once you cancel, the account returns to fully-active status.",
            ],
          },
        ],
      },
      pt: {
        intro:
          "A Granbay atende à LGPD (Lei Geral de Proteção de Dados). Você pode solicitar a exclusão da sua conta a qualquer momento em /settings/account.",
        sections: [
          {
            heading: "Como funciona",
            paragraphs: [
              "A exclusão acontece em duas fases. Primeiro, você envia uma solicitação com a justificativa. Revogamos imediatamente todas as sessões ativas e bloqueamos a conta. Começa um período de 30 dias durante o qual você pode cancelar a exclusão fazendo login novamente.",
              "Após os 30 dias, campos identificadores (email, nome de usuário, país, hash de senha, segredo 2FA) são anonimizados e a conta é totalmente excluída. Histórico de operações, lançamentos contábeis e eventos de auditoria permanecem em forma anonimizada para conformidade contábil e regulatória.",
            ],
          },
          {
            heading: "O que mantemos",
            paragraphs: [
              "Registros de operações e ordens são mantidos por exigência contábil — são necessários para reconstruir posições, tarifas e resolver disputas históricas. Após a anonimização, os registros não podem ser ligados a você.",
              "Você pode baixar um arquivo completo dos seus dados antes da exclusão em /settings/account → Baixar meus dados.",
            ],
          },
          {
            heading: "Cancelando a solicitação",
            paragraphs: [
              "Faça login novamente em qualquer momento dentro dos 30 dias. O fluxo reconhece contas com exclusão pendente e permite cancelar a solicitação sem reverificação. Após cancelar, a conta volta ao status ativo.",
            ],
          },
        ],
      },
    },
  },
  {
    slug: "fees",
    category: "fees",
    title: {
      en: "Fees",
      pt: "Tarifas",
    },
    description: {
      en: "Granbay's taker-only fee model and how fees are calculated.",
      pt: "O modelo de tarifa só-taker da Granbay e como as tarifas são calculadas.",
    },
    updatedAt: "2026-04-29",
    body: {
      en: {
        intro:
          "Granbay charges a 2% taker fee on every fill. The maker (the resting order that gets hit) pays nothing. This mirrors the Polymarket model and encourages liquidity provision.",
        sections: [
          {
            heading: "The taker is the order that crosses the spread",
            paragraphs: [
              "When you place a market order or a limit that immediately matches, you are the taker. The fee is taken from your cash at fill time — the matched share count × clearing price × 2%.",
              "When you place a limit that rests on the book until someone else matches it, you are the maker on that fill. No fee on your side.",
            ],
          },
          {
            heading: "Worked example",
            paragraphs: [
              "You market-BUY 100 YES at 50¢ = R$50 notional. Fee = R$50 × 2% = R$1.00. Total reserved from cash = R$51.00. If the matcher leaves shares unfilled, the unused fee returns proportionally.",
            ],
          },
          {
            heading: "Fees on resolution",
            paragraphs: [
              "When a market resolves, payouts are 100¢ × shares for the winning side and 0¢ for the losing side. No additional fee at resolution. Fees on void/refund follow the same logic — see the Fees article on void for details.",
            ],
          },
        ],
      },
      pt: {
        intro:
          "A Granbay cobra 2% do taker em cada execução. O maker (ordem que descansa no livro e é atingida) não paga nada. Mesmo modelo da Polymarket — incentiva fornecimento de liquidez.",
        sections: [
          {
            heading: "O taker é quem cruza o spread",
            paragraphs: [
              "Quando você envia uma ordem a mercado ou um limite que executa imediatamente, você é o taker. A tarifa sai do seu caixa no momento da execução — quantidade × preço de execução × 2%.",
              "Quando você envia um limite que descansa no livro até alguém igualar, você é o maker daquela execução. Sem tarifa do seu lado.",
            ],
          },
          {
            heading: "Exemplo prático",
            paragraphs: [
              "Você COMPRA a mercado 100 SIM a 50¢ = R$50 de valor nocional. Tarifa = R$50 × 2% = R$1,00. Total reservado do caixa = R$51,00. Se o motor de execução não preencher tudo, a tarifa não usada retorna proporcionalmente.",
            ],
          },
          {
            heading: "Tarifas na resolução",
            paragraphs: [
              "Quando um mercado é resolvido, os pagamentos são 100¢ × ações do lado vencedor e 0¢ do perdedor. Sem tarifa adicional na resolução. Tarifas em void/reembolso seguem a mesma lógica — veja o artigo de Tarifas em void.",
            ],
          },
        ],
      },
    },
  },
  {
    slug: "two-factor-authentication",
    category: "security",
    title: {
      en: "Two-factor authentication (2FA)",
      pt: "Autenticação em duas etapas (2FA)",
    },
    description: {
      en: "Set up TOTP 2FA with Google Authenticator, 1Password, etc.",
      pt: "Configure 2FA TOTP com Google Authenticator, 1Password, etc.",
    },
    updatedAt: "2026-04-29",
    body: {
      en: {
        intro:
          "Two-factor authentication adds a second layer to login. Granbay uses TOTP (Time-based One-Time Password) — compatible with any standard authenticator app.",
        sections: [
          {
            heading: "Setting it up",
            paragraphs: [
              "Go to /settings/security and tap Enable 2FA. Scan the QR code with your authenticator app and enter the 6-digit code to confirm. After this, every login requires the current 6-digit code in addition to your password.",
            ],
          },
          {
            heading: "Recovering access",
            paragraphs: [
              "If you lose access to your authenticator, contact support via /help/new with category 'account'. We require email-based verification + a manual review before resetting 2FA — for security reasons we cannot do this faster.",
            ],
          },
        ],
      },
      pt: {
        intro:
          "A autenticação em duas etapas adiciona uma camada ao login. A Granbay usa TOTP (Senha-Única-Baseada-em-Tempo) — compatível com qualquer app autenticador padrão.",
        sections: [
          {
            heading: "Como ativar",
            paragraphs: [
              "Acesse /settings/security e toque em Ativar 2FA. Escaneie o QR-code com seu app autenticador e digite o código de 6 dígitos para confirmar. Após isso, cada login exige o código atual de 6 dígitos além da senha.",
            ],
          },
          {
            heading: "Recuperando acesso",
            paragraphs: [
              "Se você perder acesso ao autenticador, contate o suporte em /help/new com categoria 'conta'. Exigimos verificação por email + revisão manual antes de resetar o 2FA — por questões de segurança não fazemos isso mais rápido.",
            ],
          },
        ],
      },
    },
  },
  {
    // EPIC-029 STORY-1700 Part-3 prerequisite (b) per ADR-019.
    // Linked from the wallet.usdc_migrated notification template
    // (apps/api/src/notifications/templates/wallet-usdc-migrated.ts).
    // Authored 2026-04-30 alongside the ADR-019 expansion.
    slug: "wallet-usdc-migration",
    category: "account",
    title: {
      en: "Your wallet now denominates in USDC",
      pt: "Sua carteira agora é em USDC",
    },
    description: {
      en: "Granbay's ledger flipped to USDC internally. Your displayed balance still shows in your local currency at the live rate.",
      pt: "O ledger da Granbay agora é em USDC internamente. Seu saldo continua aparecendo na sua moeda local na taxa atual.",
    },
    updatedAt: "2026-04-30",
    body: {
      en: {
        intro:
          "Granbay's internal accounting is now denominated in USDC, the US-dollar-pegged stablecoin. Your wallet balance was converted to USDC at a single reference rate captured at the moment of migration. Your display experience stays the same: you continue to see balances in your locale's currency (BRL, USD, EUR…) at the live exchange rate.",
        sections: [
          {
            heading: "What changed for you",
            paragraphs: [
              "The number you see in your balance pill is recomputed from your USDC balance at the live FX rate every time you open the app. If you held R$100 before the migration, you'll see ≈R$100 after it (within FX volatility — typically less than 1% intraday).",
              "Trades, fees, payouts, and order reservations are unchanged at the user-visible layer. The matching engine and ledger now operate in USDC under the hood; the display layer routes everything through the locale-resolved fiat at view time.",
            ],
          },
          {
            heading: "Why we did this",
            paragraphs: [
              "Pre-migration, every market opened in BRL liquidity carried FX-day risk on resolution — a market opened on day 0 and resolved on day 90 settled in whichever rate happened to apply when the matcher cleared. Denominating internally in USDC moves the FX boundary to deposit / withdrawal only, where the platform captures an explicit rate + spread per ADR-019.",
              "The migration also unblocks multi-currency on/off-ramps: a Brazilian user depositing R$ via PIX now lands USDC in their wallet at the partner's rate, and a US user depositing via ACH lands the same USDC at a different partner's rate — but both wallets, at the ledger level, are USDC.",
            ],
          },
          {
            heading: "Audit trail",
            paragraphs: [
              "Every wallet that was migrated has a permanent audit_events record with the pre-migration currency, pre-migration balance in cents, the conversion rate used, the timestamp of the conversion, and the resulting USDC balance. The action kind is wallet.usdc_migrated. If you ever need to verify the conversion that applied to your wallet, ask support — we can produce the audit record.",
            ],
          },
          {
            heading: "If your displayed balance looks slightly different",
            paragraphs: [
              "Small differences (under 1%) between your pre-migration balance and what you see now are FX volatility — the live rate at view time differs slightly from the migration-moment rate. This is expected behavior for the USDC-internal / fiat-displayed architecture.",
              "Larger differences (more than a few percent) are not expected. If you see one, contact support via /help/new with category 'account' and reference this article. Include a screenshot if possible.",
            ],
          },
          {
            heading: "Display preference",
            paragraphs: [
              "You can change the currency your balance displays in at /settings/display. The default is your region's currency (BRL for Brazilian users, USD elsewhere). USDC-native users can switch to displaying USDC directly to skip the live-rate conversion entirely.",
            ],
          },
        ],
      },
      pt: {
        intro:
          "A contabilidade interna da Granbay agora é em USDC, a stablecoin atrelada ao dólar americano. O saldo da sua carteira foi convertido para USDC a uma taxa única de referência capturada no momento da migração. Sua experiência de exibição não muda: você continua vendo o saldo na moeda do seu país (BRL, USD, EUR…) à taxa de câmbio atual.",
        sections: [
          {
            heading: "O que mudou para você",
            paragraphs: [
              "O número que aparece no seu indicador de saldo é recalculado a partir do saldo em USDC à taxa de câmbio atual cada vez que você abre o app. Se você tinha R$100 antes da migração, vai ver ≈R$100 depois dela (dentro da volatilidade cambial — tipicamente menos de 1% intradia).",
              "Operações, tarifas, pagamentos e reservas de ordem permanecem idênticos na camada visível ao usuário. O motor de execução e o ledger agora operam em USDC por baixo dos panos; a camada de exibição converte tudo para a moeda local resolvida no momento de exibição.",
            ],
          },
          {
            heading: "Por que fizemos isso",
            paragraphs: [
              "Antes da migração, cada mercado aberto em liquidez BRL carregava risco cambial na resolução — um mercado aberto no dia 0 e resolvido no dia 90 era liquidado em qualquer taxa vigente quando o motor de execução fechasse. Denominar internamente em USDC move a fronteira cambial para depósito / saque apenas, onde a plataforma captura uma taxa explícita + spread conforme o ADR-019.",
              "A migração também desbloqueia rampas on/off em múltiplas moedas: um usuário brasileiro depositando R$ via PIX agora recebe USDC na carteira à taxa do parceiro, e um usuário americano depositando via ACH recebe o mesmo USDC à taxa de outro parceiro — mas ambas as carteiras, no nível do ledger, são USDC.",
            ],
          },
          {
            heading: "Registro de auditoria",
            paragraphs: [
              "Cada carteira migrada tem um registro permanente em audit_events com a moeda pré-migração, o saldo pré-migração em centavos, a taxa de conversão usada, a data/hora da conversão, e o saldo USDC resultante. A ação é wallet.usdc_migrated. Se você precisar verificar a conversão aplicada à sua carteira, peça ao suporte — podemos produzir o registro.",
            ],
          },
          {
            heading: "Se o saldo exibido parecer um pouco diferente",
            paragraphs: [
              "Pequenas diferenças (menos de 1%) entre o saldo pré-migração e o que você vê agora são volatilidade cambial — a taxa atual no momento de exibição difere ligeiramente da taxa do momento da migração. Esse é o comportamento esperado para a arquitetura USDC-interno / fiat-exibido.",
              "Diferenças maiores (mais de alguns por cento) não são esperadas. Se você ver uma, contate o suporte em /help/new com categoria 'conta' e cite este artigo. Inclua uma captura de tela se possível.",
            ],
          },
          {
            heading: "Preferência de exibição",
            paragraphs: [
              "Você pode alterar a moeda em que seu saldo é exibido em /settings/display. O padrão é a moeda da sua região (BRL para usuários brasileiros, USD para os demais). Usuários nativos em USDC podem trocar para exibir USDC diretamente, pulando a conversão à taxa atual.",
            ],
          },
        ],
      },
    },
  },
  {
    // EPIC-029 STORY-1716 Part 4 — USDC-as-truth + BRL projection
    // documentation. The deposit modal shows a one-time educational
    // tooltip that links here on first deposit, so the article is
    // the long-form version of the "Saldo: $X USDC (≈ R$Y,YY hoje)"
    // display rule users see everywhere.
    slug: "usdc-and-brl-display",
    category: "account",
    title: {
      en: "Why your balance is in USDC",
      pt: "Por que seu saldo é em USDC",
    },
    description: {
      en: "Granbay denominates every balance, trade, and fee in USDC internally. Any local-fiat value shown alongside is a real-time projection at the current exchange rate.",
      pt: "Granbay denomina cada saldo, operação e taxa em USDC internamente. O valor em reais que você vê é uma projeção em tempo real que muda com o câmbio.",
    },
    updatedAt: "2026-05-05",
    body: {
      en: {
        intro:
          "All balances, trades, fees, and resolutions on Granbay are denominated in USDC, a stablecoin pegged to the US dollar. If you live in a region where Granbay also displays a local-fiat projection (e.g. Brazilian users see a BRL projection in pt-BR), that projection is a real-time conversion at the current exchange rate — it can change. Your USDC balance is what you actually hold.",
        sections: [
          {
            heading: "What you see vs. what you own",
            paragraphs: [
              'Your balance card shows your USDC amount. Depending on your region, an optional local-fiat projection ("≈ $Y CAD" / "≈ R$Y" / "≈ ₹Y") may appear next to it. The USDC number is what you actually hold; the local-fiat number is what that USDC would be worth right now if you converted it back.',
              "Trade prices, market resolutions, and fees use the same dual-display when a local-fiat projection is enabled for your region.",
            ],
          },
          {
            heading: "Why USDC as the unit of account",
            paragraphs: [
              "USDC is a stablecoin issued by Circle and pegged 1:1 to the US dollar. Using USDC as the platform's unit of account means a market resolution 30 days out doesn't expose Granbay to FX swings between deposit and payout. It also lets us reconcile every balance and ledger entry in one canonical currency.",
              "When you deposit in your local fiat, it converts to USDC at the locked exchange rate at the moment you confirm payment. When you withdraw, USDC converts back to your local fiat at the locked rate at confirmation. The platform fee + a small spread cover the cost of running the conversion.",
            ],
          },
          {
            heading: "The exchange-rate risk is on you",
            paragraphs: [
              "If your local fiat strengthens against the dollar between your deposit and your withdrawal, you'll see less local fiat coming back than you put in. The opposite is true if your local fiat weakens. This is by design — your USDC balance is fixed; only the local-fiat projection moves.",
              "If you'd rather not see any local-fiat projection, switch the display currency to USDC under /settings/display. The platform never converts your balance to local fiat on its own; the projection is purely visual.",
            ],
            bullets: [
              "USDC = your actual balance, fixed.",
              "Local fiat = real-time projection, changes with FX.",
              "Conversion happens only at deposit + withdrawal time.",
            ],
          },
          {
            heading: "Where the FX rate comes from",
            paragraphs: [
              "Granbay sources USDC ↔ local-fiat rates from a tiered feed: Circle's USDC reference rate (primary), regional exchange aggregates as secondary fallbacks, and Coingecko as a final fallback. The platform refreshes the rate every 60 seconds.",
              "If every upstream source goes stale at once, Granbay surfaces an admin alert and continues to honor any quote that was already locked in for an in-flight deposit or withdrawal — but you won't be able to start a new conversion until the feed recovers.",
            ],
          },
        ],
      },
      pt: {
        intro:
          "Todos os saldos, operações, taxas e liquidações na Granbay são denominados em USDC, uma stablecoin atrelada ao dólar dos Estados Unidos. O valor em reais ao lado do seu saldo em USDC é uma projeção em tempo real à taxa de câmbio atual — pode variar.",
        sections: [
          {
            heading: "O que você vê vs. o que você tem",
            paragraphs: [
              'O card de saldo mostra dois números: "Saldo: $X USDC (≈ R$Y,YY hoje)". O número em USDC é o que você efetivamente possui; o número em reais é quanto esse USDC valeria agora se você convertesse de volta.',
              "O valor em R$ atualiza conforme a taxa BRL/USDC se movimenta. Preços de operações, liquidações de mercados e taxas usam a mesma exibição dupla.",
            ],
          },
          {
            heading: "Por que USDC e não BRL diretamente",
            paragraphs: [
              "USDC é uma stablecoin emitida pela Circle e atrelada 1:1 ao dólar. Usar USDC como unidade interna significa que um mercado que liquida em 30 dias não expõe a Granbay a oscilações de câmbio entre depósito e pagamento. Também permite reconciliar todo saldo e lançamento contábil em uma única moeda canônica.",
              "Seu depósito converte BRL → USDC à taxa travada no momento em que você confirma o pagamento. Seu saque converte USDC → BRL à taxa travada na confirmação. A taxa da plataforma + um pequeno spread cobrem o custo da conversão.",
            ],
          },
          {
            heading: "O risco de câmbio é seu",
            paragraphs: [
              "Se o real se valorizar frente ao dólar entre seu depósito e seu saque, você verá menos reais voltando do que colocou. O contrário é verdadeiro se o real se desvalorizar. Isso é por design — seu saldo em USDC é fixo; apenas a projeção em reais varia.",
              "Se você prefere não ver a projeção em reais, troque a moeda de exibição para USDC em /settings/display. A plataforma nunca converte seu saldo para reais por conta própria; a projeção é puramente visual.",
            ],
            bullets: [
              "USDC = seu saldo real, fixo.",
              "BRL = projeção em tempo real, varia com o câmbio.",
              "Conversão acontece apenas em depósitos e saques.",
            ],
          },
          {
            heading: "De onde vem a taxa de câmbio",
            paragraphs: [
              "A Granbay obtém a taxa USDC/BRL de um feed em camadas: a taxa de referência USDC da Circle (primária), uma mediana agregada de três exchanges brasileiras (Mercado Bitcoin + Bitso + Foxbit) quando a Circle está desatualizada, e o Coingecko como fallback final. A plataforma atualiza a taxa a cada 60 segundos.",
              "Se todas as fontes upstream ficarem desatualizadas ao mesmo tempo, a Granbay dispara um alerta administrativo e continua honrando qualquer cotação já travada para depósitos/saques em andamento — mas você não conseguirá iniciar uma nova conversão até o feed se recuperar.",
            ],
          },
        ],
      },
    },
  },
  {
    // STORY-1717b Part 1 — companion article for FxSpreadDisclosure.
    // Linked from the disclosure's expanded breakdown footer + the
    // /help index. Long-form rationale for the 0.50% spread + how
    // it's locked at quote time.
    slug: "fx-spread",
    category: "account",
    title: {
      en: "How the FX spread works",
      pt: "Como funciona o spread de câmbio",
    },
    description: {
      en: "Granbay charges a 0.50% spread on BRL ↔ USDC conversions at deposit and withdrawal. The rate you see is the rate you pay — locked at quote time, no hidden mid-market markup behind the scenes.",
      pt: "A Granbay cobra um spread de 0,50% nas conversões BRL ↔ USDC em depósitos e saques. A taxa que você vê é a taxa que você paga — travada no momento da cotação, sem markup escondido sobre a taxa mid-market.",
    },
    updatedAt: "2026-05-10",
    body: {
      en: {
        intro:
          "When you deposit BRL or withdraw to BRL, Granbay converts at a rate that includes a 0.50% spread over the mid-market USDC/BRL price. The rate displayed on the deposit/withdrawal screen IS the rate the platform applies — there's no hidden markup. The spread is locked at quote time and holds for the duration of the conversion.",
        sections: [
          {
            heading: "Why a spread, not a flat fee",
            paragraphs: [
              "Every BRL ↔ USDC conversion has a real cost to Granbay — banking-rail fees, custody/treasury rebalancing through Bitso OTC, and operational overhead. A 0.50% spread is the simplest way to recover that cost while keeping the displayed rate predictable.",
              "Flat fees disadvantage small users (a R$5 fee on a R$50 deposit is 10%); percentage fees scale fairly. The 0.50% spread is small enough that users moving R$100 pay R$0,50, and large enough to cover the per-transaction processing cost.",
            ],
          },
          {
            heading: "What 'locked at quote time' means",
            paragraphs: [
              "When you confirm a deposit or withdrawal, Granbay locks the exchange rate at that exact moment. The BRL amount you confirm is the BRL amount that moves — no surprise rate change between confirm and bank settlement.",
              "If the rate moves between your quote and the bank actually moving the money, Granbay absorbs the difference. The 0.50% spread compensates the platform for taking on that quote-window risk.",
            ],
            bullets: [
              "Quote shown = rate applied.",
              "Spread baked into the rate, not added as a separate line.",
              "Granbay absorbs the rate movement during the quote window.",
            ],
          },
          {
            heading: "See the breakdown",
            paragraphs: [
              "The deposit and withdrawal screens show a single combined rate by default — the rate you'll actually pay. Click 'See breakdown' to expand the mid-market rate, the spread amount, and the rate you're getting, side by side. Both views describe the same transaction; the combined view is calmer, the breakdown is more transparent.",
            ],
          },
        ],
      },
      pt: {
        intro:
          "Quando você deposita em reais ou saca para reais, a Granbay converte a uma taxa que inclui um spread de 0,50% sobre o preço mid-market do USDC/BRL. A taxa exibida na tela de depósito/saque É a taxa que a plataforma aplica — sem markup escondido. O spread é travado no momento da cotação e vale durante toda a conversão.",
        sections: [
          {
            heading: "Por que um spread, em vez de uma taxa fixa",
            paragraphs: [
              "Toda conversão BRL ↔ USDC tem um custo real para a Granbay — taxas dos trilhos bancários, rebalanceamento de tesouraria via Bitso OTC, e overhead operacional. Um spread de 0,50% é a forma mais simples de recuperar esse custo mantendo a taxa exibida previsível.",
              "Taxas fixas prejudicam usuários pequenos (R$5 sobre um depósito de R$50 é 10%); taxas percentuais escalam de forma justa. O spread de 0,50% é pequeno o bastante para que quem move R$100 pague R$0,50, e grande o bastante para cobrir o custo de processamento por transação.",
            ],
          },
          {
            heading: "O que significa 'travado no momento da cotação'",
            paragraphs: [
              "Quando você confirma um depósito ou saque, a Granbay trava a taxa de câmbio naquele exato momento. O valor em reais que você confirma é o valor que se move — sem surpresa de mudança de taxa entre a confirmação e a liquidação bancária.",
              "Se a taxa se mover entre sua cotação e a movimentação efetiva do banco, a Granbay absorve a diferença. O spread de 0,50% compensa a plataforma por assumir esse risco da janela de cotação.",
            ],
            bullets: [
              "Cotação exibida = taxa aplicada.",
              "Spread embutido na taxa, não somado como linha separada.",
              "A Granbay absorve a oscilação durante a janela de cotação.",
            ],
          },
          {
            heading: "Veja o detalhamento",
            paragraphs: [
              "As telas de depósito e saque mostram uma taxa combinada única por padrão — a taxa que você efetivamente paga. Clique em 'Ver detalhamento' para expandir a taxa mid-market, o valor do spread e a sua taxa final, lado a lado. As duas visões descrevem a mesma operação; a combinada é mais limpa, o detalhamento é mais transparente.",
            ],
          },
        ],
      },
    },
  },
];

export function findArticle(slug: string): HelpArticle | undefined {
  return HELP_ARTICLES.find((a) => a.slug === slug);
}

export function articlesByCategory(category: HelpCategory): HelpArticle[] {
  return HELP_ARTICLES.filter((a) => a.category === category);
}
